import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion } from 'framer-motion';
import { Eye, Gauge, Target, Shield, Settings, TrendingUp } from 'lucide-react';

const icons = [Eye, Gauge, Target, Shield, Settings, TrendingUp];

export default function BenefitsSection() {
  const { lang } = useLanguage();
  const t = translations.benefits[lang];

  return (
    <section id="benefits" data-testid="benefits-section" className="relative py-24 md:py-32 bg-enbi-offwhite overflow-hidden">
      {/* Diagonal accent */}
      <div
        className="absolute bottom-0 right-0 w-[45%] h-full bg-enbi-navy/[0.03]"
        style={{ clipPath: 'polygon(40% 0, 100% 0, 100% 100%, 0 100%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-[2px] bg-enbi-gold" />
            <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">{t.label}</span>
          </div>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-enbi-navy uppercase tracking-tight">
            {t.headline}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`benefit-card-${i}`}
                className="group flex gap-5"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-enbi-navy flex items-center justify-center group-hover:bg-enbi-gold transition-colors duration-400">
                  <Icon className="text-enbi-gold group-hover:text-enbi-navy transition-colors duration-400" size={22} strokeWidth={1.25} />
                </div>
                <div>
                  <h3 className="font-outfit text-enbi-navy text-sm tracking-[0.15em] uppercase font-semibold mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
